import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Trash2, Save, Edit2, Check, Download, X,
  Eye, Grid, List, Star, Image as ImageIcon, FileText, Layers,
  Crop, Palette, Package
} from 'lucide-react';
import axiosInstance from '../config/axios';
import { API_BASE_URL } from '../types/review';

interface DesignAsset {
  id: number;
  asset_type: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  metadata?: any;
}

interface ContentMetadata {
  design_type?: string;
  file_format?: string;
  dimensions?: string;
  [key: string]: any;
}

export default function DesignAssetEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Asset management
  const [assets, setAssets] = useState<DesignAsset[]>([]);
  const [editingAssetId, setEditingAssetId] = useState<number | null>(null);
  const [editedName, setEditedName] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  // View options
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedAsset, setSelectedAsset] = useState<DesignAsset | null>(null);

  // Content metadata editing
  const [editingContent, setEditingContent] = useState(false);
  const [contentTitle, setContentTitle] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [contentMetadata, setContentMetadata] = useState<ContentMetadata>({});

  useEffect(() => {
    fetchContent();
  }, [id]);

  const fetchContent = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/content/${id}`);
      const data = response.data;

      setContent(data);
      setContentTitle(data.title || '');
      setContentDescription(data.description || '');

      // Parse metadata
      let metadata: ContentMetadata = {};
      try {
        metadata = data.metadata ? JSON.parse(data.metadata) : {};
      } catch (e) {
        console.warn('Failed to parse metadata');
      }
      setContentMetadata(metadata);

      // Set assets
      const designAssets = data.assets || [];
      setAssets(designAssets);

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadingFiles(files);
    }
  };

  const handleUpload = async () => {
    if (uploadingFiles.length === 0 || !content) return;

    setSaving(true);
    try {
      const assetType = contentMetadata.design_type === 'logo_contest'
        ? 'logo_contest'
        : 'design_asset';

      for (const file of uploadingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('content_id', content.id);
        formData.append('asset_type', assetType);

        await axiosInstance.post('/assets/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setUploadingFiles([]);
      await fetchContent(); // Reload to show new assets
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (assetId: number, newName: string) => {
    if (!newName.trim()) return;

    setSaving(true);
    try {
      await axiosInstance.put(`/assets/${assetId}`, {
        file_name: newName.trim()
      });

      setAssets(assets.map(a =>
        a.id === assetId ? { ...a, file_name: newName.trim() } : a
      ));

      setEditingAssetId(null);
      setEditedName('');
    } catch (err: any) {
      alert('Rename failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    setSaving(true);
    try {
      await axiosInstance.delete(`/assets/${assetId}`);
      setAssets(assets.filter(a => a.id !== assetId));

      if (selectedAsset?.id === assetId) {
        setSelectedAsset(null);
      }
    } catch (err: any) {
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (asset: DesignAsset) => {
    const url = `${API_BASE_URL}/assets/${asset.id}/download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = asset.file_name;
    link.click();
  };

  const handleSaveContent = async () => {
    if (!content) return;

    setSaving(true);
    try {
      const updatedMetadata = {
        ...contentMetadata,
        design_type: contentMetadata.design_type || 'graphic'
      };

      await axiosInstance.put(`/content/${content.id}`, {
        title: contentTitle,
        description: contentDescription,
        metadata: JSON.stringify(updatedMetadata)
      });

      setContent({
        ...content,
        title: contentTitle,
        description: contentDescription,
        metadata: JSON.stringify(updatedMetadata)
      });

      setEditingContent(false);
    } catch (err: any) {
      alert('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (asset: DesignAsset) => {
    setEditingAssetId(asset.id);
    setEditedName(asset.file_name);
  };

  const cancelEdit = () => {
    setEditingAssetId(null);
    setEditedName('');
  };

  const getDesignTypeIcon = (type?: string) => {
    if (!type) return Palette;
    switch (type.toLowerCase()) {
      case 'logo_contest': return Star;
      case 'banner': return Crop;
      case 'template': return Layers;
      case 'graphic': return ImageIcon;
      case 'infographic': return FileText;
      case 'icons': return Grid;
      case 'illustration': return Palette;
      case 'presentation': return FileText;
      default: return Palette;
    }
  };

  const getAssetUrl = (asset: DesignAsset) => {
    return `${API_BASE_URL}/assets/${asset.id}/download`;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Content not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const DesignTypeIcon = getDesignTypeIcon(contentMetadata.design_type);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <DesignTypeIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  {editingContent ? (
                    <input
                      type="text"
                      value={contentTitle}
                      onChange={(e) => setContentTitle(e.target.value)}
                      className="text-xl font-semibold text-gray-900 border-b-2 border-blue-500 focus:outline-none"
                      autoFocus
                    />
                  ) : (
                    <h1 className="text-xl font-semibold text-gray-900">{content.title}</h1>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {contentMetadata.design_type || 'Design'} • {assets.length} asset{assets.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editingContent ? (
                <>
                  <button
                    onClick={handleSaveContent}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4 inline mr-1" />
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingContent(false);
                      setContentTitle(content.title);
                      setContentDescription(content.description || '');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditingContent(true)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <Edit2 className="w-4 h-4 inline mr-1" />
                    Edit Info
                  </button>
                  <button
                    onClick={() => navigate(`/dashboard/content/${id}/preview`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Eye className="w-4 h-4 inline mr-1" />
                    Preview
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          {editingContent && (
            <div className="mt-4">
              <textarea
                value={contentDescription}
                onChange={(e) => setContentDescription(e.target.value)}
                placeholder="Add description..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upload Assets</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex-1">
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,.psd,.ai,.svg,.sketch,.fig,.xd"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 cursor-pointer transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {uploadingFiles.length > 0
                    ? `${uploadingFiles.length} file${uploadingFiles.length !== 1 ? 's' : ''} selected`
                    : 'Click to select files or drag and drop'
                  }
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Images, PDFs, PSD, AI, SVG, Sketch, Figma, XD
                </p>
                {uploadingFiles.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500 max-w-md mx-auto truncate">
                    {uploadingFiles.map(f => f.name).join(', ')}
                  </div>
                )}
              </div>
            </label>
          </div>

          {uploadingFiles.length > 0 && (
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleUpload}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {saving ? 'Uploading...' : `Upload ${uploadingFiles.length} File${uploadingFiles.length !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => setUploadingFiles([])}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Assets Display */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Design Assets ({assets.length})
            </h2>
          </div>

          {assets.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No assets uploaded yet</p>
              <p className="text-sm text-gray-400 mt-1">Upload your design files above</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  className={`relative bg-white border-2 rounded-lg cursor-pointer transition-all ${
                    selectedAsset?.id === asset.id
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-50 rounded-t-lg overflow-hidden p-4 flex items-center justify-center">
                    <img
                      src={getAssetUrl(asset)}
                      alt={asset.file_name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-3 border-t">
                    {editingAssetId === asset.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(asset.id, editedName);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRename(asset.id, editedName);
                            }}
                            className="flex-1 p-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            <Check className="w-3 h-3 mx-auto" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelEdit();
                            }}
                            className="flex-1 p-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            <X className="w-3 h-3 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-gray-900 truncate mb-1">{asset.file_name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(asset.file_size)}</p>
                        <div className="flex gap-1 mt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(asset);
                            }}
                            className="flex-1 p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Download"
                          >
                            <Download className="w-3 h-3 mx-auto" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startEdit(asset);
                            }}
                            className="flex-1 p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Rename"
                          >
                            <Edit2 className="w-3 h-3 mx-auto" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(asset.id);
                            }}
                            className="flex-1 p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 mx-auto" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y">
              {assets.map((asset) => (
                <div key={asset.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className="flex-none w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={getAssetUrl(asset)}
                        alt={asset.file_name}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      {editingAssetId === asset.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            className="flex-1 px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(asset.id, editedName);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <button
                            onClick={() => handleRename(asset.id, editedName)}
                            disabled={saving}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-gray-900">{asset.file_name}</p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(asset.file_size)} • {asset.mime_type || 'Unknown type'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {editingAssetId !== asset.id && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(asset)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(asset)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Rename"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          disabled={saving}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
